// Radar view: the network around the phone, rotated so "up" is the way the
// phone faces. Pure canvas, no sensors of its own (heading.ts feeds it).
//
// 2026-10-08 rewrite ("bits and piecey" - each pipe used to be one short bar
// at its nearest point): pipes are now drawn as their real geometry,
// projected around the user, coloured by network (drinking water blue,
// stormwater green, wastewater red - assetStyle.ts, the same as AR / map /
// list), line width by diameter, pipe ends as small nodes, valves /
// hydrants / fittings as their symbols. Range rings at round distances, the
// GPS accuracy disc, a north marker, and the "Guide me" target (a marker in
// range, an edge arrow + distance when beyond it).
//
// Performance on a phone: geometry is converted to local metres once per
// data / position change (culled to the range, simplified to half a pixel);
// each frame only rotates those points. A requestAnimationFrame loop eases
// the drawn heading toward the compass (smooth rotation, no 4 Hz jumps) and
// draws only when something changed. Tap-to-identify hit-tests the drawn
// lines and symbols.
import { angleDiff, compassPoint } from "./heading";
import { assetColour, drawSymbol, hexA, symbolOf } from "./assetStyle";
import type { ValveStatus } from "./assetStyle";
import type { LonLat, XY } from "./geoMath";
import { cullToRange, edgePoint, fmtDistance, pipeWidthPx, pxToPolyline, radarXY, ringStep, simplify, toLocal, turnDeg } from "./geoMath";

export interface RadarAsset {
  id: string;
  type: string;
  network?: string | null;
  water_type?: string | null;
  label: string;
  path?: LonLat[] | null;
  lon?: number | null;
  lat?: number | null;
  diameter_mm?: number | null;
  normal_status?: string | null;
}

export interface RadarTarget {
  lat: number;
  lon: number;
  label: string;
  arrived: boolean;
}

export interface RadarState {
  origin: { lat: number; lon: number } | null;
  heading: number | null;
  accuracy: number | null;
  selected: string | null;
  rangeM: number;
  /** assets to emphasise (the open project's pipes) */
  emphasis?: Set<string> | null;
  target?: RadarTarget | null;
}

interface Shape {
  id: string;
  colour: string;
  width: number;
  runs: XY[][]; // local metres (east, north) from the origin
  point: XY | null;
  sym: ReturnType<typeof symbolOf>;
  status?: ValveStatus;
}

/** Colour of an asset by NETWORK (kept for callers). */
export function typeColour(t: string, network?: string | null): string {
  return assetColour({ asset_type: t, network });
}

// --- cached geometry --------------------------------------------------------

let shapes: Shape[] = [];
let shapesKey: { assets: RadarAsset[]; lat: number; lon: number; range: number } | null = null;

function buildShapes(assets: RadarAsset[], origin: { lat: number; lon: number }, rangeM: number, pxPerM: number): Shape[] {
  const tol = 0.5 / Math.max(pxPerM, 0.01); // half a pixel, in metres
  const reach = rangeM * 1.45; // the square canvas' corners show a little beyond the ring
  const out: Shape[] = [];
  for (const a of assets) {
    const sym = symbolOf(a.type);
    const colour = assetColour({ asset_type: a.type, network: a.network, water_type: a.water_type });
    if (a.path && a.path.length >= 2) {
      const local = a.path.map((p) => toLocal(origin.lat, origin.lon, p[0], p[1]));
      const runs = cullToRange(local, reach).map((r) => simplify(r, tol)).filter((r) => r.length >= 2);
      if (!runs.length) continue;
      out.push({ id: a.id, colour, width: pipeWidthPx(a.diameter_mm), runs, point: null, sym: "line" });
    } else if (typeof a.lon === "number" && typeof a.lat === "number") {
      const p = toLocal(origin.lat, origin.lon, a.lon, a.lat);
      if (Math.hypot(p[0], p[1]) > reach) continue;
      out.push({ id: a.id, colour, width: 0, runs: [], point: p, sym: sym === "line" ? "dot" : sym,
        status: a.type === "valve" ? (a.normal_status as ValveStatus | undefined) : undefined });
    }
  }
  return out;
}

// --- drawing ----------------------------------------------------------------

/** Where things were drawn on the last frame (CSS px within the canvas). */
let hitLines: { id: string; pts: XY[]; width: number }[] = [];
let hitPoints: { id: string; x: number; y: number }[] = [];

function drawFrame(canvas: HTMLCanvasElement, st: RadarState, assets: RadarAsset[], heading: number | null): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const size = Math.round(Math.min(canvas.clientWidth || 320, canvas.clientHeight || canvas.clientWidth || 320));
  if (canvas.width !== Math.round(size * dpr) || canvas.height !== Math.round(size * dpr)) {
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const c = size / 2;
  const rad = c - 18;
  const range = st.rangeM;
  const scale = rad / range;
  ctx.clearRect(0, 0, size, size);
  // background disc
  ctx.fillStyle = "#0f1a24";
  ctx.beginPath();
  ctx.arc(c, c, rad + 14, 0, Math.PI * 2);
  ctx.fill();

  hitLines = [];
  hitPoints = [];
  const origin = st.origin;
  if (origin) {
    const k = shapesKey;
    if (!k || k.assets !== assets || k.range !== range || Math.hypot(...toLocal(k.lat, k.lon, origin.lon, origin.lat)) > 0.5) {
      shapes = buildShapes(assets, origin, range, scale);
      shapesKey = { assets, lat: origin.lat, lon: origin.lon, range };
    }
  } else {
    shapes = [];
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, rad + 13, 0, Math.PI * 2);
  ctx.clip();
  // GPS accuracy disc
  if (st.accuracy !== null) {
    ctx.fillStyle = "rgba(66,153,225,0.16)";
    ctx.beginPath();
    ctx.arc(c, c, Math.min(rad + 13, st.accuracy * scale), 0, Math.PI * 2);
    ctx.fill();
  }
  // range rings at round distances
  const step = ringStep(range);
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1;
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.font = "11px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  for (let r = step; r <= range + 0.01; r += step) {
    ctx.beginPath();
    ctx.arc(c, c, r * scale, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillText(`${r} m`, c + 4, c - r * scale + 12);
  }
  // facing wedge (always up)
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.beginPath();
  ctx.moveTo(c, c);
  ctx.arc(c, c, rad, -Math.PI / 2 - Math.PI / 8, -Math.PI / 2 + Math.PI / 8);
  ctx.closePath();
  ctx.fill();

  // pipes: casing pass then colour pass, so crossings read cleanly; the
  // selected one last and brighter
  const toPx = (p: XY): XY => radarXY(p[0], p[1], heading, scale, c, c);
  const lines = shapes.filter((s) => s.runs.length);
  const order = (s: Shape) => (s.id === st.selected ? 2 : st.emphasis?.has(s.id) ? 1 : 0);
  lines.sort((a, b) => order(a) - order(b));
  const projected = lines.map((s) => ({ s, runs: s.runs.map((r) => r.map(toPx)) }));
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const { s, runs } of projected) {
    const sel = s.id === st.selected;
    const emph = !!st.emphasis?.has(s.id);
    ctx.strokeStyle = sel ? "#fefcbf" : "rgba(15,26,36,0.9)";
    ctx.lineWidth = s.width + (sel ? 6 : 2.5);
    for (const r of runs) strokeRun(ctx, r);
    ctx.strokeStyle = sel || emph || !st.selected ? s.colour : hexA(s.colour, 0.75);
    ctx.lineWidth = s.width + (emph ? 1.5 : 0);
    for (const r of runs) strokeRun(ctx, r);
    for (const r of runs) hitLines.push({ id: s.id, pts: r, width: s.width });
  }
  // pipe ends: small nodes (joints / where pipes meet)
  for (const { s, runs } of projected) {
    const local = s.runs;
    ctx.fillStyle = s.colour;
    ctx.strokeStyle = "#0f1a24";
    ctx.lineWidth = 1;
    for (let i = 0; i < runs.length; i++) {
      for (const [lp, pp] of [[local[i][0], runs[i][0]], [local[i][local[i].length - 1], runs[i][runs[i].length - 1]]] as [XY, XY][]) {
        if (Math.hypot(lp[0], lp[1]) > range * 1.45) continue;
        ctx.beginPath();
        ctx.arc(pp[0], pp[1], Math.max(2, s.width * 0.55), 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
  }
  // point assets (valves, hydrants, fittings, lids ...), selected on top
  const pts = shapes.filter((s) => s.point).sort((a, b) => order(a) - order(b));
  for (const s of pts) {
    const [x, y] = toPx(s.point as XY);
    const sel = s.id === st.selected;
    drawSymbol(ctx, s.sym, x, y, sel ? 9 : 6.5, s.colour, 1, s.status);
    if (sel) {
      ctx.strokeStyle = "#fefcbf";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 13, 0, Math.PI * 2);
      ctx.stroke();
    }
    hitPoints.push({ id: s.id, x, y });
  }
  ctx.restore();

  // north marker on the rim
  const north = heading === null ? 0 : -heading;
  const nx = c + Math.sin((north * Math.PI) / 180) * (rad + 8);
  const ny = c - Math.cos((north * Math.PI) / 180) * (rad + 8);
  ctx.fillStyle = "#fc8181";
  ctx.font = "bold 13px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("N", nx, ny);

  // the "Guide me" target
  if (st.target && origin) {
    const t = toLocal(origin.lat, origin.lon, st.target.lon, st.target.lat);
    const dist = Math.hypot(t[0], t[1]);
    if (dist <= range) {
      const [x, y] = toPx(t);
      drawTarget(ctx, x, y, st.target.arrived);
    } else {
      const brg = (Math.atan2(t[0], t[1]) * 180) / Math.PI;
      const rel = heading === null ? brg : turnDeg(brg, heading);
      const [ex, ey] = edgePoint(rel, c, c, rad - 4);
      drawEdgeArrow(ctx, ex, ey, rel, fmtDistance(dist));
    }
  }

  // me + facing tick
  ctx.fillStyle = "#4299e1";
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(c, c - 13);
  ctx.lineTo(c + 5, c - 6);
  ctx.lineTo(c - 5, c - 6);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(c, c, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
}

function strokeRun(ctx: CanvasRenderingContext2D, r: XY[]): void {
  ctx.beginPath();
  ctx.moveTo(r[0][0], r[0][1]);
  for (let i = 1; i < r.length; i++) ctx.lineTo(r[i][0], r[i][1]);
  ctx.stroke();
}

function drawTarget(ctx: CanvasRenderingContext2D, x: number, y: number, arrived: boolean): void {
  ctx.save();
  ctx.strokeStyle = arrived ? "#68d391" : "#f6e05e";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(x, y, 11, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - 17, y);
  ctx.lineTo(x - 6, y);
  ctx.moveTo(x + 6, y);
  ctx.lineTo(x + 17, y);
  ctx.moveTo(x, y - 17);
  ctx.lineTo(x, y - 6);
  ctx.moveTo(x, y + 6);
  ctx.lineTo(x, y + 17);
  ctx.stroke();
  ctx.restore();
}

function drawEdgeArrow(ctx: CanvasRenderingContext2D, x: number, y: number, relDeg: number, text: string): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((relDeg * Math.PI) / 180);
  ctx.fillStyle = "#f6e05e";
  ctx.strokeStyle = "#0f1a24";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -12);
  ctx.lineTo(9, 6);
  ctx.lineTo(0, 1);
  ctx.lineTo(-9, 6);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  // the distance label, pulled in toward the centre so it stays on the canvas
  const a = (relDeg * Math.PI) / 180;
  const lx = x - Math.sin(a) * 24;
  const ly = y + Math.cos(a) * 24;
  ctx.font = "bold 12px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#0f1a24";
  ctx.strokeText(text, lx, ly);
  ctx.fillStyle = "#f6e05e";
  ctx.fillText(text, lx, ly);
}

// --- the animation loop -----------------------------------------------------

interface Loop {
  canvas: HTMLCanvasElement;
  get: () => { state: RadarState; assets: RadarAsset[] };
  shown: number | null; // the heading currently drawn (eased toward the compass)
  last: number;
  dirty: boolean;
  raf: number | null;
}

let loop: Loop | null = null;

/** Start drawing `canvas` every animation frame it needs (stops itself when
 * the canvas leaves the page). `get` returns the current state + assets. */
export function startRadar(canvas: HTMLCanvasElement, get: () => { state: RadarState; assets: RadarAsset[] }): void {
  stopRadar();
  const l: Loop = { canvas, get, shown: null, last: performance.now(), dirty: true, raf: null };
  loop = l;
  const tick = (now: number) => {
    if (loop !== l) return;
    if (!canvas.isConnected) {
      loop = null;
      return;
    }
    const { state, assets } = get();
    const dt = Math.min(200, now - l.last);
    l.last = now;
    const target = state.heading;
    let moved = false;
    if (target === null) {
      moved = l.shown !== null;
      l.shown = null;
    } else if (l.shown === null) {
      l.shown = target;
      moved = true;
    } else {
      const diff = turnDeg(target, l.shown);
      if (Math.abs(diff) > 0.15) {
        // ease: ~90 % of the way in 250 ms, whatever the frame rate
        l.shown = (l.shown + diff * (1 - Math.exp(-dt / 110)) + 360) % 360;
        moved = true;
      }
    }
    if (moved || l.dirty) {
      l.dirty = false;
      drawFrame(canvas, state, assets, l.shown);
    }
    l.raf = requestAnimationFrame(tick);
  };
  l.raf = requestAnimationFrame(tick);
}

/** Something other than the heading changed (data, selection, fix, range). */
export function radarDirty(): void {
  if (loop) loop.dirty = true;
}

export function stopRadar(): void {
  if (loop?.raf != null) cancelAnimationFrame(loop.raf);
  loop = null;
}

/** The asset drawn nearest a tap on the radar canvas (CSS px): symbols
 * within 24 px win over lines (within 16 px of the stroke). */
export function radarHitTest(x: number, y: number, maxPx = 24): string | null {
  let best: string | null = null;
  let bd = maxPx;
  for (const p of hitPoints) {
    const d = Math.hypot(x - p.x, y - p.y);
    if (d <= bd) {
      best = p.id;
      bd = d;
    }
  }
  if (best) return best;
  bd = 16;
  for (const l of hitLines) {
    const d = pxToPolyline(x, y, l.pts) - l.width / 2;
    if (d <= bd) {
      best = l.id;
      bd = d;
    }
  }
  return best;
}

/** "7 m ahead-left" style text for the selected asset. */
export function directionText(distance: number, bearing: number | null, heading: number | null): string {
  if (bearing === null) return `${Math.round(distance)} m (right here)`;
  if (heading === null) return `${Math.round(distance)} m to the ${compassPoint(bearing)}`;
  const rel = angleDiff(bearing, heading);
  const a = Math.abs(rel);
  const side = rel < 0 ? "left" : "right";
  const where = a <= 20 ? "ahead" : a <= 70 ? `ahead-${side}` : a <= 110 ? `to your ${side}` : a <= 160 ? `behind-${side}` : "behind you";
  return `${Math.round(distance)} m ${where}`;
}

export function arrowRotation(bearing: number | null, heading: number | null): number {
  if (bearing === null) return 0;
  return heading === null ? bearing : angleDiff(bearing, heading);
}
